// src/components/configuration/sections/MeetingProviderPreference.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Label } from '../../ui/label';
import { Spinner } from '../../ui/spinner';

// Hooks
import { useTeams } from '../../../hooks/useTeams';
import { useZoom } from '../../../hooks/useZoom';
import { useSocket } from '../../../contexts/SocketContext';

/**
 * Meeting Provider Preference Component
 *
 * Allows users to choose their preferred meeting provider when both Teams and Zoom are connected.
 * Only renders when BOTH providers are connected.
 *
 * Uses existing settings infrastructure:
 * - Setting key: 'preferred_meeting_provider'
 * - Values: 'teams' | 'zoom'
 * - Default: 'teams'
 */
const MeetingProviderPreference = () => {
  const socket = useSocket();
  const { connected: teamsConnected } = useTeams();
  const { connected: zoomConnected } = useZoom();

  // Local state
  const [preference, setPreference] = useState('teams');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Only render if BOTH providers are connected
  if (!teamsConnected || !zoomConnected) {
    return null;
  }

  // ================================
  // LOAD CURRENT PREFERENCE
  // ================================

  useEffect(() => {
    // Listen for settings loaded
    const handleSettingsLoaded = (data) => {
      if (data.settings && data.settings.preferred_meeting_provider) {
        setPreference(data.settings.preferred_meeting_provider);
      } else {
        // Use default
        setPreference('teams');
      }
      setLoading(false);
    };

    const handleSettingsLoadFailed = (data) => {
      console.error('❌ Failed to load settings:', data);
      setError('Failed to load preference');
      setLoading(false);
    };

    // Subscribe to events
    socket.on('settings_loaded', handleSettingsLoaded);
    socket.on('get_settings_failed', handleSettingsLoadFailed);

    // Request current settings
    socket.emit('get_settings', { keys: ['preferred_meeting_provider'] });

    // Cleanup
    return () => {
      socket.off('settings_loaded', handleSettingsLoaded);
      socket.off('get_settings_failed', handleSettingsLoadFailed);
    };
  }, [socket]);

  // ================================
  // HANDLE PREFERENCE CHANGE
  // ================================

  useEffect(() => {
    // Listen for settings update confirmation
    const handleSettingsUpdated = (data) => {
      console.log('✅ Settings updated:', data);
      setSaving(false);
      setError(null);
    };

    const handleUpdateFailed = (data) => {
      console.error('❌ Failed to update settings:', data);
      setError('Failed to save preference');
      setSaving(false);
    };

    socket.on('settings_updated', handleSettingsUpdated);
    socket.on('update_settings_failed', handleUpdateFailed);

    return () => {
      socket.off('settings_updated', handleSettingsUpdated);
      socket.off('update_settings_failed', handleUpdateFailed);
    };
  }, [socket]);

  const handlePreferenceChange = (value) => {
    console.log('🎯 Changing meeting provider preference to:', value);
    setPreference(value);
    setSaving(true);
    setError(null);

    socket.emit('update_settings', {
      settings: {
        preferred_meeting_provider: value
      }
    });
  };

  // ================================
  // RENDER
  // ================================

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        <span>Loading preference...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground flex-shrink-0">Preferred provider:</span>
        <RadioGroup
          value={preference}
          onValueChange={handlePreferenceChange}
          disabled={saving}
          className="flex items-center gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="teams" id="teams" />
            <Label htmlFor="teams" className="text-sm cursor-pointer">Microsoft Teams</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="zoom" id="zoom" />
            <Label htmlFor="zoom" className="text-sm cursor-pointer">Zoom</Label>
          </div>
        </RadioGroup>
        {saving && <Spinner className="size-4 text-muted-foreground" />}
      </div>
      <p className="text-xs text-muted-foreground">
        If the selected provider fails, the other will be tried automatically.
      </p>
    </div>
  );
};

export default MeetingProviderPreference;
